import { useContext } from 'react';
import { faThumbTackSlash } from '@fortawesome/free-solid-svg-icons';
import { WidgetContainerContext, WidgetContainer } from '@/core/widgets/export';
import { authService, useSessionId } from '@/core/auth/export';
import {
  analyticsService,
  AnalyticsEvent,
  AnalyticsSubTypeEvent,
  AnalyticsTypes,
} from '@/shared/analytics/export';
import { useIsMobile } from '@/shared/hooks/export';
import { MenuListItem, MenuListPages } from '@/shared/components/export';
import type { PinnedWidgetPreferences } from '@/modules/articles/widgets/pinned/pinned-series-widget';

const COLORS: { value: string; label: string }[] = [
  { value: 'note-yellow', label: 'Yellow' },
  { value: 'note-blue', label: 'Blue' },
  { value: 'note-green', label: 'Green' },
  { value: 'note-orange', label: 'Orange' },
];

/** The ellipsis-menu content (color swatches + unpin) shared by PinnedSeriesWidget and PinnedArticleWidget — both are sticky notes with the same color-preference and unpin behavior, just a different default color and analytics sub-type. */
export default function PinnedWidgetMenuActions({
  close,
  defaultColor,
  colorChangeEvent,
}: {
  close: () => void;
  defaultColor: string;
  colorChangeEvent: AnalyticsSubTypeEvent;
}) {
  const widgetContainer = useContext<WidgetContainer>(WidgetContainerContext);
  const sessionId = useSessionId();
  const subject = authService.getSubjectData();
  // Mobile has no real dashboard grid to unpin from (matches dashboard.tsx's
  // own d-md-none/d-md-flex mobile/desktop split), so "Unpin" only makes
  // sense on desktop.
  const isMobile = useIsMobile(768);

  const selectedColor =
    widgetContainer.getPreferences<PinnedWidgetPreferences>()?.color ?? defaultColor;

  const selectColor = (color: string) => {
    analyticsService.recordAnalyticsEvent(AnalyticsEvent.WIDGET_CONFIG_CHANGED, {
      subject,
      object: {
        type: AnalyticsTypes.WIDGET,
        eventSubType: colorChangeEvent,
        id: widgetContainer.widgetId,
        config: { color },
      },
      session: { id: sessionId },
    });
    widgetContainer.updatePreferences<PinnedWidgetPreferences>({ color });
  };

  const items: MenuListItem[] = [
    {
      key: 'color',
      label: 'Color',
      collapsible: COLORS.map(({ value, label }) => ({
        key: value,
        label,
        iconElement: (
          <span
            className={`${value}-picker`}
            style={{ width: 14, height: 14, borderRadius: '50%', display: 'inline-block' }}
          />
        ),
        checked: value === selectedColor,
        onSelect: () => selectColor(value),
      })),
    },
    ...(isMobile
      ? []
      : [
          { key: 'unpin-divider', label: '', divider: true },
          {
            key: 'unpin',
            label: 'Unpin',
            icon: faThumbTackSlash,
            danger: true,
            onSelect: widgetContainer.close,
          },
        ]),
  ];

  return <MenuListPages items={items} close={close} />;
}
